import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, getDoc } from 'firebase/firestore';
import { getEffectiveUserId } from './userService';
import { fetchProjectDetails } from './projectService';

const eventsCollection = collection(db, "events");

// Fonction pour récupérer tous les événements
export const fetchEvents = async () => {
  const userId = await getEffectiveUserId();
  const eventsQuery = query(eventsCollection, where("userId", "==", userId));
  const querySnapshot = await getDocs(eventsQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [];
};

export const fetchEventsByProjectId = async (projectId) => {
  const userId = await getEffectiveUserId();
  const eventsQuery = query(
    eventsCollection,
    where("projectId", "==", projectId),
    where("userId", "==", userId)
  );
  const querySnapshot = await getDocs(eventsQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const fetchEventsByGlobal = async () => {
  const userId = await getEffectiveUserId();
  const eventsQuery = query(
    eventsCollection,
    where("userId", "==", userId),
    where("showInGlobalPlanning", "==", true)
  );
  const querySnapshot = await getDocs(eventsQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const fetchEventById = async (eventId) => {
  const eventDoc = doc(db, "events", eventId);
  const eventSnapshot = await getDoc(eventDoc);
  if (eventSnapshot.exists()) {
    return { id: eventSnapshot.id, ...eventSnapshot.data() };
  } else {
    throw new Error("Événement non trouvé");
  }
};

export const updateAllEvents = async () => {
  const querySnapshot = await getDocs(eventsCollection);
  const events = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  for (const event of events) {
    if (!event.id) continue;
    const eventRef = doc(db, "events", event.id);
    await updateDoc(eventRef, { ...event, projectSection: event.projectSection || "" });
    await updateDoc(eventRef, { ...event, accessTime: event.accessTime || "00:00" });
  }
};

// Fonction pour ajouter un nouvel événement
export const addEvent = async (event) => {
  const userId = await getEffectiveUserId();
  if (!event) return;

  const accessTime = event.accessTime || "00:00";
  const [hours, minutes] = accessTime.split(':').map(Number);
  const accessTimeInMs = (hours * 60 + minutes) * 60 * 1000;

  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);

  const newAccessEvent = { 
    ressources: event.ressources,
    title: event.title,
    startDate: new Date(startDate.getTime() - accessTimeInMs - 1).toString(),
    endDate: new Date(endDate.getTime() + accessTimeInMs).toString(),
    clientId: event.clientId,
    projectId: event.projectId,
    isFinished: false,
    color: "#000077",
    noEdit: true,
    showInGlobalPlanning: true,
    userId
  };
  const accessRef = await addDoc(eventsCollection, newAccessEvent);
  await updateEvent({ id: accessRef.id, ...newAccessEvent });

  const newEvent = { ...event, accessId: accessRef.id, userId };
  const docRef = await addDoc(eventsCollection, newEvent);
  return [{ id: docRef.id, ...newEvent }, { id: accessRef.id, ...newAccessEvent }];
};

// Fonction pour mettre à jour un événement existant
export const updateEvent = async (event) => {
  if (!event.id || !event) {
    return;
  }

  // Si l'event a des tâches, on met à jour isFinished
  if (Array.isArray(event.tasks) && event.tasks.length > 0) {
    event.isFinished = event.tasks.every(task => task.isFinished);
  }

  const eventRef = doc(db, "events", event.id);
  await updateDoc(eventRef, { ...event });

  // Si l'event est fini, on envoie un mail au responsable du projet
  if (event.isFinished && event.projectId) {
    await notifyProjectManagerEventFinished(event);
  }
};

export const deleteEvent = async (event) => {
  try {
    // Référence au document de l'événement
    const eventDoc = doc(db, "events", event.id);

    // Supprimer le document
    await deleteDoc(eventDoc);

    if (event.accessId && typeof event.accessId === "string" && event.accessId.length > 0) {
      const accessEventDoc = doc(db, "events", event.accessId);
      await deleteDoc(accessEventDoc);
    }
  } catch (error) {
    console.error("Erreur lors de la suppression de l'événement :", error);
  }
};

async function notifyProjectManagerEventFinished(event) {
  // Récupère les détails du projet
  const project = await fetchProjectDetails(event.projectId);
  if (!project || !project.managerId) return;

  // Récupère la ressource manager
  const managerRef = doc(db, "ressources", project.managerId);
  const managerSnap = await getDoc(managerRef);
  if (!managerSnap.exists()) return;
  const manager = managerSnap.data();
  if (!manager.mailPro) return;

  // Récupère tous les events du projet et de la même section
  const eventsQuery = query(
    eventsCollection,
    where("projectId", "==", event.projectId),
    where("projectSection", "==", event.projectSection || "")
  );
  const querySnapshot = await getDocs(eventsQuery);
  const events = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Liste des events non finis (hors celui qu'on vient de finir)
  const unfinished = events.filter(e => !e.isFinished && e.id !== event.id);

  // Prépare le contenu du mail
  let body = `L'événement "${event.title}" de ${project.name} est terminé.\n\n`;
  if (unfinished.length > 0) {
    body += "Événements non terminés dans la même section :\n";
    unfinished.forEach(e => {
      body += `- ${e.title}\n`;
    });
  } else {
    body += "Tous les événements de cette section sont terminés.";
  }

  // Appel à l’API d’envoi de mail
  await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: "Nailit",
      email: "contact@nailit-planning.com",
      type: "Notification",
      message: body,
      to: manager.mailPro
    })
  });
}
